import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, InputGroup } from "react-bootstrap";
import { FaSearch, FaEdit } from "react-icons/fa";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
  getCountFromServer,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import NextPrevPagination from "./NextPrevPagination";
import AddCurrencyModal from "./AddCurrencyModal";
import CurrencyEditModal from "./CurrencyEditModal";

const CurrencyTable = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localSearch, setLocalSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDocs, setPageDocs] = useState([null]);
  const [totalCount, setTotalCount] = useState(0);
  const [editCurrencyData, setEditCurrencyData] = useState(null);

  const itemsPerPage = 20;

  // Debounce search input to avoid excessive Firestore reads
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(localSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [localSearch]);

  const buildQueryConstraints = (isCount = false, startDoc = null) => {
    const constraints = [];

    // Order by code is always required
    constraints.push(orderBy("code", sortOrder));

    const qVal = searchQuery.trim().toUpperCase();
    if (qVal) {
      if (sortOrder === "desc") {
        constraints.push(startAt(qVal + "\uf8ff"));
        constraints.push(endAt(qVal));
      } else {
        constraints.push(startAt(qVal));
        constraints.push(endAt(qVal + "\uf8ff"));
      }
    }

    if (!isCount) {
      if (startDoc) {
        constraints.push(startAfter(startDoc));
      }
      constraints.push(limit(itemsPerPage));
    }

    return constraints;
  };

  const fetchTotalCount = async () => {
    try {
      const constraints = buildQueryConstraints(true);
      const q = query(collection(db, "currencies"), ...constraints);
      const snapshot = await getCountFromServer(q);
      setTotalCount(snapshot.data().count);
    } catch (error) {
      console.error("Error fetching currencies count:", error);
      setTotalCount(0);
    }
  };

  const fetchPage = async (pageNumber, startDoc) => {
    try {
      setLoading(true);
      const constraints = buildQueryConstraints(false, startDoc);
      const q = query(collection(db, "currencies"), ...constraints);
      const snapshot = await getDocs(q);

      const list = snapshot.docs.map((docSnap) => ({
        ...docSnap.data(),
      }));

      setCurrencies(list);

      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      setPageDocs((prev) => {
        const next = [...prev];
        next[pageNumber] = lastDoc;
        return next;
      });
    } catch (error) {
      console.error("Error fetching currencies page:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReload = async () => {
    setPageDocs([null]);
    setCurrentPage(1);
    await fetchTotalCount();
    await fetchPage(1, null);
  };

  useEffect(() => {
    handleReload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, sortOrder]);

  const handleToggleEnable = async (code, currentStatus) => {
    try {
      const currencyRef = doc(db, "currencies", code);
      const newStatus = !currentStatus;
      await updateDoc(currencyRef, {
        enabled: newStatus,
        updatedAt: serverTimestamp(),
      });
      setCurrencies((prev) =>
        prev.map((c) =>
          c.code === code ? { ...c, enabled: newStatus, updatedAt: { seconds: Date.now() / 1000 } } : c
        )
      );
    } catch (error) {
      console.error("Error toggling currency status:", error);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      fetchPage(prevPage, pageDocs[prevPage - 1]);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchPage(nextPage, pageDocs[nextPage - 1]);
    }
  };

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

  const formatUpdated = (updatedAt) => {
    if (!updatedAt) return "-";
    let date;
    if (updatedAt.toDate) {
      date = updatedAt.toDate();
    } else if (updatedAt.seconds) {
      date = new Date(updatedAt.seconds * 1000);
    } else {
      date = new Date(updatedAt);
    }
    return isNaN(date.getTime()) ? "-" : date.toLocaleString();
  };

  return (
    <section className="px-1 py-4 nav-body-bg btm-rad">
      <AddCurrencyModal onAddSuccess={handleReload} />

      <Container>
        {/* Header and Controls */}
        <Row className="align-items-center justify-content-between flex-column flex-md-row text-center text-md-start mb-4">
          <Col xs="auto" className="mb-3 mb-md-0">
            <h3 className="text-h2">Currencies</h3>
          </Col>

          {/* Search Box */}
          <Col md={4} className="mb-3 mb-md-0">
            <InputGroup className="theme-search-group">
              <Form.Control
                type="text"
                placeholder="Search by currency code"
                className="db-set-style border-0 shadow-none"
                style={{ backgroundColor: "transparent" }}
                value={localSearch}
                onChange={(e) => {
                  setLocalSearch(e.target.value);
                }}
              />
              <InputGroup.Text className="bg-transparent border-0">
                <FaSearch style={{ color: "var(--accent-lime)" }} />
              </InputGroup.Text>
            </InputGroup>
          </Col>

          {/* Sorting and Add Button */}
          <Col xs="auto" className="d-flex align-items-center gap-2 justify-content-center">
            <select
              className="form-select border-1 px-2"
              style={{ width: "120px", padding: "3px 0px", borderColor: "#3b6353" }}
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
              }}
            >
              <option value="asc">Code (A-Z)</option>
              <option value="desc">Code (Z-A)</option>
            </select>

            <button
              className="btn btn-primary px-3"
              style={{ padding: "3px 0px" }}
              data-bs-toggle="modal"
              data-bs-target="#addCurrencyModal"
            >
              + Add Currency
            </button>
          </Col>
        </Row>

        {/* Table View */}
        <div
          className="table-responsive scrollable-table-y mt-2 bg-transparent"
          style={{ minHeight: "60vh", overflowY: "auto" }}
        >
          <style>
            {`
              @media (max-width: 768px) {
                .scrollable-table-y {
                  max-height: 400px;
                  overflow-y: auto;
                }
              }
              .form-switch .form-check-input {
                width: 2.5em;
                height: 1.25em;
                cursor: pointer;
              }
              .form-switch .form-check-input:checked {
                background-color: #1A73E8;
                border-color: #1A73E8;
              }
            `}
          </style>

          <Table className="table-hover align-middle body-text-two mb-0">
            <thead className="sticky-top top-0" style={{ zIndex: 10 }}>
              <tr>
                <th className="transparent-header text-center text-white fw-bold">SL</th>
                <th className="transparent-header text-white fw-bold">Currency</th>
                <th className="transparent-header text-center text-white fw-bold">Rate to GBP</th>
                <th className="transparent-header text-center text-white fw-bold">Status</th>
                <th className="transparent-header text-center text-white fw-bold">Updated</th>
                <th className="transparent-header text-center text-white fw-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 transparent-header">
                    <div className="admin-loader"></div>
                  </td>
                </tr>
              ) : currencies.length > 0 ? (
                currencies.map((c, index) => (
                  <tr key={c.code || index} style={{ borderBottom: "1px solid #8CB6EF" }}>
                    <td className="py-3 transparent-header text-center text-white">
                      {indexOfFirstItem + index + 1}
                    </td>
                    <td className="py-3 transparent-header text-white fw-semibold">
                      {c.code}
                    </td>
                    <td className="py-3 transparent-header text-center text-white font-monospace">
                      {c.exchangeRate !== undefined ? Number(c.exchangeRate).toFixed(4) : "-"}
                    </td>
                    <td className="py-3 transparent-header text-center">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={c.enabled ?? true}
                            onChange={() => handleToggleEnable(c.code, c.enabled ?? true)}
                            style={{ margin: 0 }}
                          />
                        </div>
                        <span
                          className={`fw-semibold small ${
                            c.enabled ?? true ? "text-success" : "text-danger"
                          }`}
                          style={{ width: "65px", textAlign: "left" }}
                        >
                          {c.enabled ?? true ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 transparent-header text-center text-white small">
                      {formatUpdated(c.updatedAt)}
                    </td>
                    <td className="py-3 transparent-header text-center">
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          className="btn btn-link text-warning p-0"
                          style={{ outline: "none", boxShadow: "none" }}
                          onClick={() => setEditCurrencyData(c)}
                          title="Edit Currency"
                        >
                          <FaEdit size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5 transparent-header text-white">
                    No currencies found. Add a currency to get started!
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4 pb-3">
            <NextPrevPagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPrev={handlePrev}
              onNext={handleNext}
            />
          </div>
        )}
      </Container>

      {/* Edit Modal */}
      <CurrencyEditModal
        currency={editCurrencyData}
        setCurrency={setEditCurrencyData}
        onEditSuccess={handleReload}
      />
    </section>
  );
};

export default CurrencyTable;
